import { useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { EVENT_CATEGORIES, type EventCategory } from '@mds/shared/types/event';
import { Button } from '@mds/shared/ui/shadcn/button';
import { Checkbox } from '@mds/shared/ui/shadcn/checkbox';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@mds/shared/ui/shadcn/empty';

import EventCard from '@/components/card/event-card';
import { MediaCardRow, MediaCardSkeleton } from '@/components/card/media-card';
import FilterDropdown from '@/components/form/filter-dropdown';
import PageHeader from '@/components/heading/page-header';
import { InfiniteCardList } from '@/components/list/infinite-card-list';
import { ErrorBlock } from '@/components/states';

import { eventQueries } from '@/features/events/events.queries';

const band = 'mx-auto max-w-page px-gutter pt-section';

const ALL = '';

const NOUN = { one: 'event', other: 'events' };

/**
 * The filter bar is hidden for now. Its code stays wired up so it comes back
 * by setting this to true; with it off, no filter is ever set and the list
 * shows every upcoming event.
 */
const SHOW_FILTERS = false;

export default function EventsPage() {
  const [kind, setKind] = useState<string>(ALL);
  const [freeOnly, setFreeOnly] = useState(false);

  // Filtering and paging are the server's: it returns the matching events a
  // page at a time, soonest first, with the total and the dropdown's counts.
  // A filter change is a new key, so the list starts again from page 1.
  const events = useInfiniteQuery(
    eventQueries.infinite({
      category: kind === ALL ? undefined : (kind as EventCategory),
      pricing: freeOnly ? 'free' : undefined,
    }),
  );

  const shown = events.data?.pages.flatMap((page) => page.data) ?? [];
  const firstPage = events.data?.pages[0];
  const total = firstPage?.total ?? 0;
  const counts = firstPage?.counts;

  // Every category the server counted, in the canonical order. The counts
  // ignore the category and pricing filters, so the options do not shift as
  // the visitor picks one.
  const kindOptions = [
    { value: ALL, label: 'All categories', count: counts?.all ?? 0 },
    ...EVENT_CATEGORIES.filter((category) => counts?.byCategory[category]).map((category) => ({
      value: category as string,
      label: category as string,
      count: counts?.byCategory[category] ?? 0,
    })),
  ];

  const filtered = kind !== ALL || freeOnly;

  return (
    <>
      <section className={band}>
        <PageHeader
          title="Events & workshops"
          description="Learn a skill, share an idea or just come and look around. Join our open houses, workshops, roundtables and studio clinics throughout the year."
        />
      </section>

      {/* Hidden for now: see SHOW_FILTERS. */}
      {SHOW_FILTERS && (
        <section className="mx-auto max-w-page px-gutter pt-block" aria-label="Filter events">
          <div className="flex flex-wrap items-end gap-4">
            <FilterDropdown
              label="Category"
              options={kindOptions}
              value={kind}
              onChange={setKind}
            />

            {/* A checkbox rather than a switch: shadcn's `switch` is not
              installed, and a filter that reloads a list is a checkbox anyway.
              `pb-1.5` centres it on the 36px filter box beside it. */}
            <label className="flex cursor-pointer items-center gap-2.5 pb-1.5 text-base">
              <Checkbox
                checked={freeOnly}
                onCheckedChange={(checked) => setFreeOnly(checked === true)}
              />
              Free only
            </label>
          </div>

          <p className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-ink-secondary">
            <span>
              Showing <b className="font-semibold text-ink">{shown.length}</b> of {total}
            </span>
            {filtered && (
              <Button
                variant="link"
                className="h-auto p-0 text-sm"
                onClick={() => {
                  setKind(ALL);
                  setFreeOnly(false);
                }}
              >
                Clear filters
              </Button>
            )}
          </p>
        </section>
      )}

      <section className="mx-auto max-w-page px-gutter pt-block">
        {/* The first page's states stand in for the whole list; every page
            after it is the list's own business. */}
        {events.isPending ? (
          <MediaCardRow>
            {Array.from({ length: 3 }, (_, i) => (
              <MediaCardSkeleton key={i} variant="framed" />
            ))}
          </MediaCardRow>
        ) : !events.data ? (
          <ErrorBlock
            title="We couldn't load the events"
            error={events.error}
            onRetry={() => void events.refetch()}
          />
        ) : shown.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{filtered ? 'No events match' : 'No upcoming events'}</EmptyTitle>
              <EmptyDescription>
                {filtered
                  ? 'Nothing in the programme fits those filters. Clear them to see everything.'
                  : 'Nothing is on the programme yet. Check back soon.'}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          // One grid, soonest first, with no month headings — the date is on
          // every card's meta row, so the list reads in order without them.
          <InfiniteCardList
            items={shown}
            total={total}
            query={events}
            noun={NOUN}
            skeletonVariant="framed"
            renderItem={(event) => <EventCard key={event.id} event={event} />}
          />
        )}
      </section>
    </>
  );
}
